import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Spin, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getIntlLocale } from "util/locale";
import { fetchWelcomeWidgetMetricReviews } from "queries/welcomeWidgetMetricReviews";
import WelcomeDrawerReviewsList from "pages/Welcome/WelcomeDrawerReviewsList";
import {
  WELCOME_REVIEWS_DRAWER_SHARED_PROPS,
  welcomeReviewsDrawerWidth,
} from "pages/Welcome/welcomeReviewsDrawerProps";
import { useWelcomeReviewsDrawerCloseEffect } from "pages/Welcome/useWelcomeReviewsDrawerCloseEffect";
import { parseInsightSectionsFromPlainText } from "pages/Welcome/welcomeKeyDriverInsightText";
import styles from "./PivonyInsightWidget.module.scss";

function normText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

function parseNonNegativeInt(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function resolveTopicReviewCounts({ tab, representativeReviewIds, analyzedSampleSize }) {
  const citedCount =
    Array.isArray(representativeReviewIds) && representativeReviewIds.length > 0
      ? representativeReviewIds.length
      : 0;
  const tabScope = parseNonNegativeInt(tab?.review_count ?? tab?.reviewCount);
  const analyzed = parseNonNegativeInt(analyzedSampleSize);
  let scopeReviewCount = 0;
  if (tabScope != null && tabScope > 0) {
    scopeReviewCount = tabScope;
  } else if (citedCount > 0) {
    scopeReviewCount = citedCount;
  } else if (analyzed != null && analyzed > 0) {
    scopeReviewCount = analyzed;
  }
  const citedReviewCount = citedCount > 0 ? citedCount : null;
  const showCitedAsPrimary =
    citedReviewCount != null &&
    (tabScope == null || tabScope <= 0 || scopeReviewCount === citedReviewCount);
  return {
    scopeReviewCount,
    citedReviewCount,
    showCitedAsPrimary,
  };
}

function parseRepresentativeReviewIdsFromItem(item) {
  if (!item || typeof item !== "object") return [];
  const raw =
    item.representative_review_ids ??
    item.representativeReviewIds ??
    item.representative_reviews ??
    item.representativeReviews;
  if (Array.isArray(raw)) {
    const out = [];
    const seen = new Set();
    for (const x of raw) {
      const s = String(x ?? "").trim();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
      if (out.length >= 80) break;
    }
    return out;
  }
  if (typeof raw === "string") {
    const out = [];
    const seen = new Set();
    for (const s of raw.split(/[\s,]+/)) {
      const t = s.trim();
      if (!t || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
      if (out.length >= 80) break;
    }
    return out;
  }
  return [];
}

function titleCaseWords(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const STATUS_STYLES = {
  Critical: {
    labelKey: "welcome.pivonyInsightStatusCritical",
    classKey: "badgeCritical",
  },
  Koru: {
    labelKey: "welcome.pivonyInsightStatusKeep",
    classKey: "badgeKoru",
  },
  Yuksek: {
    labelKey: "welcome.pivonyInsightStatusImprove",
    classKey: "badgeYuksek",
  },
  Orta: {
    labelKey: "welcome.pivonyInsightStatusMonitor",
    classKey: "badgeYuksek",
  },
};

const QUADRANT_CELL = {
  Urgent: { row: 0, col: 0 },
  Keep: { row: 0, col: 1 },
  Improve: { row: 1, col: 0 },
  Monitor: { row: 1, col: 0 },
  Strategic: { row: 1, col: 1 },
};

const QUADRANT_LABELS = [
  ["Urgent", "Keep"],
  ["Improve", "Strategic"],
];

const quadrantDotClasses = {
  Urgent: "dotUrgent",
  Keep: "dotKeep",
  Improve: "dotImprove",
  Monitor: "dotImprove",
  Strategic: "dotStrategic",
};

function formatPercent(value, locale) {
  return `${Number(value || 0).toLocaleString(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })}%`;
}

function formatImportanceScore(value, locale) {
  return Number(value || 0).toLocaleString(locale, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
}

const METRIC_INFO_COPY = {
  performance: {
    introKey: "welcome.pivonyInsightMetricPerformanceInfoIntro",
    stepKeys: [
      "welcome.pivonyInsightMetricPerformanceInfoStep1",
      "welcome.pivonyInsightMetricPerformanceInfoStep2",
      "welcome.pivonyInsightMetricPerformanceInfoStep3",
    ],
    summaryKey: "welcome.pivonyInsightMetricPerformanceInfoSummary",
  },
  importance: {
    introKey: "welcome.pivonyInsightMetricImportanceInfoIntro",
    stepKeys: [
      "welcome.pivonyInsightMetricImportanceInfoStep1",
      "welcome.pivonyInsightMetricImportanceInfoStep2",
      "welcome.pivonyInsightMetricImportanceInfoStep3",
    ],
    summaryKey: "welcome.pivonyInsightMetricImportanceInfoSummary",
  },
  negativity: {
    introKey: "welcome.pivonyInsightMetricNegativityInfoIntro",
    stepKeys: [
      "welcome.pivonyInsightMetricNegativityInfoStep1",
      "welcome.pivonyInsightMetricNegativityInfoStep2",
      "welcome.pivonyInsightMetricNegativityInfoStep3",
    ],
    summaryKey: "welcome.pivonyInsightMetricNegativityInfoSummary",
  },
};

function buildMetricInfoTooltip(t, copy) {
  return (
    <div className={styles.metricInfoTooltip}>
      <div className={styles.metricInfoTooltipHeading}>
        {t("welcome.pivonyInsightMeaningTitle")}
      </div>
      <p className={styles.metricInfoTooltipIntro}>{t(copy.introKey)}</p>
      <ul className={styles.metricInfoTooltipList}>
        {copy.stepKeys.map((key) => (
          <li key={key}>{t(key)}</li>
        ))}
      </ul>
      <p className={styles.metricInfoTooltipSummary}>{t(copy.summaryKey)}</p>
    </div>
  );
}

function MetricLabelWithInfo({ label, metricKey, t }) {
  const copy = METRIC_INFO_COPY[metricKey];
  return (
    <>
      {copy ? (
        <Tooltip
          title={buildMetricInfoTooltip(t, copy)}
          placement="top"
          overlayInnerStyle={{ maxWidth: 360, textAlign: "left" }}
        >
          <button
            type="button"
            className={styles.metricInfoButton}
            aria-label={t("welcome.pivonyInsightMetricInfoAria")}
          >
            <InfoCircleOutlined />
          </button>
        </Tooltip>
      ) : null}
      <div className={styles.metricLabel}>{label}</div>
    </>
  );
}

function getStatusMeta(status) {
  return STATUS_STYLES[status] || STATUS_STYLES.Orta;
}

function getQuadrantPosition(quadrant) {
  return QUADRANT_CELL[quadrant] || QUADRANT_CELL.Strategic;
}

function detectQuadrantFromMetrics(perf, importance, negativity) {
  const p = Number(perf || 0);
  const i = Number(importance || 0);
  const n = Number(negativity || 0);
  if ((i >= 12 && p < 70) || n >= 35) return "Urgent";
  if (p >= 85 && n <= 10) return "Keep";
  if (i >= 10) return "Improve";
  return "Strategic";
}

function statusFromQuadrant(quadrant) {
  if (quadrant === "Urgent") return "Critical";
  if (quadrant === "Keep") return "Koru";
  if (quadrant === "Improve") return "Yuksek";
  return "Orta";
}

function parseInsightSections(rawText) {
  return parseInsightSectionsFromPlainText(rawText);
}

function buildCategoriesFromItems(llmItems, tabs, fallbackSummary) {
  const topicTabs = (Array.isArray(tabs) ? tabs : []).filter((tab) => {
    const id = String(tab?.id || "").toLowerCase();
    return tab && id !== "general";
  });
  const tabById = new Map(topicTabs.map((tab) => [String(tab.id), tab]));
  const tabByLabel = new Map(
    topicTabs.map((tab, index) => [normText(tab.label || tab.id || `topic-${index + 1}`), tab]),
  );

  const mapped = (Array.isArray(llmItems) ? llmItems : [])
    .filter((item) => item && typeof item === "object")
    .map((item, index) => {
      const itemTopicId = String(item.topic_id || item.topicId || item.tab_id || "").trim();
      const itemTopicLabel = String(item.topic_label || item.topicLabel || item.topic || "").trim();
      const tab =
        tabById.get(itemTopicId) ||
        tabByLabel.get(normText(itemTopicLabel)) ||
        null;
      const parsedLegacy = parseInsightSections(item.description || item.summary || fallbackSummary);
      const finding = String(item.finding || item.critical_finding || parsedLegacy.finding || "").trim();
      const meaning = String(item.meaning || item.interpretation || parsedLegacy.meaning || "").trim();
      const action = String(item.action || item.recommended_action || parsedLegacy.action || "").trim();

      const llmQuadrant = String(item.quadrant || "").trim();
      const llmStatus = String(item.status || "").trim();
      const perfRaw = item.performance ?? item.perf;
      const impRaw = item.importance;
      const negRaw = item.negativity ?? item.negative_sentiment_percentage;
      const sampleRaw = item.sample_size ?? item.sampleSize;

      const perf = Number(perfRaw);
      const importance = Number(impRaw);
      const negativity = Number(negRaw);
      const sampleSize = Number(sampleRaw);

      const representativeReviewIds = parseRepresentativeReviewIdsFromItem(item);
      const reviewCounts = resolveTopicReviewCounts({
        tab,
        representativeReviewIds,
        analyzedSampleSize: sampleSize,
      });

      const quadrant =
        llmQuadrant ||
        detectQuadrantFromMetrics(
          Number.isFinite(perf) ? perf : 0,
          Number.isFinite(importance) ? importance : 0,
          Number.isFinite(negativity) ? negativity : 0,
        );
      const status = llmStatus || statusFromQuadrant(quadrant);

      const topicSelectKey = String(tab?.id || itemTopicId || `topic-${index + 1}`);

      return {
        id: `${topicSelectKey}__row_${index}`,
        topicSelectKey,
        name: titleCaseWords(tab?.label || itemTopicLabel || item.title || `Topic ${index + 1}`),
        perf: Number.isFinite(perf) ? perf : 0,
        importance: Number.isFinite(importance) ? importance : 0,
        negativity: Number.isFinite(negativity) ? negativity : 0,
        scopeReviewCount: reviewCounts.scopeReviewCount,
        citedReviewCount: reviewCounts.citedReviewCount,
        showCitedAsPrimary: reviewCounts.showCitedAsPrimary,
        sampleSize: reviewCounts.scopeReviewCount,
        representativeReviewIds,
        finding,
        meaning,
        action,
        pending: Boolean(tab?.payload_pending || tab?.content_pending),
        quadrant,
        status,
      };
    });

  if (mapped.length > 0) return mapped;
  return [];
}

function resolveTopicLibIdForReviews(widget, tabKey) {
  const s = String(tabKey ?? "").trim();
  if (!s || s.toLowerCase() === "general") return null;
  if (s.startsWith("topic_")) {
    const rest = s.slice("topic_".length);
    const n = Number(rest);
    if (!Number.isFinite(n)) return null;
    const tids = Array.isArray(widget?.topic_ids)
      ? widget.topic_ids
          .map((x) => Number(x))
          .filter((x) => Number.isFinite(x) && x > 0)
      : [];
    if (tids.includes(n)) return n;
    if (n >= 1 && n <= tids.length) return tids[n - 1];
    return null;
  }
  if (!s.startsWith("topic-")) return null;
  const rest = s.slice("topic-".length);
  const n = Number(rest);
  if (!Number.isFinite(n)) return null;
  const tids = Array.isArray(widget?.topic_ids)
    ? widget.topic_ids.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
    : [];
  if (tids.includes(n)) return n;
  if (n >= 1 && n <= tids.length) return tids[n - 1];
  return null;
}

function buildCategories(rows, tabs, fallbackSummary) {
  const fromItems = buildCategoriesFromItems(rows, tabs, fallbackSummary);
  if (fromItems.length > 0) return fromItems;

  const topicTabs = (Array.isArray(tabs) ? tabs : []).filter((tab) => {
    const id = String(tab?.id || "").toLowerCase();
    return tab && id !== "general";
  });
  const tabByLabel = new Map(
    topicTabs.map((tab, index) => [normText(tab.label || tab.id || `topic-${index + 1}`), tab]),
  );
  const groupedRows = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = normText(row?.topic);
    if (!key) return;
    const prev = groupedRows.get(key);
    if (!prev) {
      groupedRows.set(key, {
        topic: String(row.topic),
        performance: Number(row.performance || 0),
        importance: Number(row.importance || 0),
        negativity: Number(row.negative_sentiment_percentage || 0),
        sample: Number(row.sample_size || 0),
        count: 1,
      });
      return;
    }
    prev.performance += Number(row.performance || 0);
    prev.importance += Number(row.importance || 0);
    prev.negativity += Number(row.negative_sentiment_percentage || 0);
    prev.sample += Number(row.sample_size || 0);
    prev.count += 1;
  });

  const fromRows = Array.from(groupedRows.entries()).map(([key, agg], index) => {
    const tab = tabByLabel.get(key);
    const metricPerf = agg.performance / Math.max(1, agg.count);
    const metricImportance = agg.importance / Math.max(1, agg.count);
    const metricNegativity = agg.negativity / Math.max(1, agg.count);
    const parsed = parseInsightSections(tab?.content || fallbackSummary);
    const quadrant = detectQuadrantFromMetrics(metricPerf, metricImportance, metricNegativity);
    const topicSelectKey = String(tab?.id || `topic-${index + 1}`);
    const reviewCounts = resolveTopicReviewCounts({
      tab,
      representativeReviewIds: [],
      analyzedSampleSize: agg.sample,
    });
    return {
      id: `${topicSelectKey}__row_${index}`,
      topicSelectKey,
      name: titleCaseWords(tab?.label || agg.topic),
      perf: metricPerf,
      importance: metricImportance,
      negativity: metricNegativity,
      scopeReviewCount: reviewCounts.scopeReviewCount,
      citedReviewCount: reviewCounts.citedReviewCount,
      showCitedAsPrimary: reviewCounts.showCitedAsPrimary,
      sampleSize: reviewCounts.scopeReviewCount,
      representativeReviewIds: [],
        finding: parsed.finding,
      meaning: parsed.meaning,
      action: parsed.action,
      pending: Boolean(tab?.payload_pending || tab?.content_pending),
      quadrant,
      status: statusFromQuadrant(quadrant),
    };
  });

  if (fromRows.length > 0) return fromRows;

  return topicTabs.map((tab, index) => {
    const parsed = parseInsightSections(tab?.content || fallbackSummary);
    const topicSelectKey = String(tab?.id || `topic-${index + 1}`);
    const reviewCounts = resolveTopicReviewCounts({
      tab,
      representativeReviewIds: [],
      analyzedSampleSize: tab?.review_count,
    });
    return {
      id: `${topicSelectKey}__row_${index}`,
      topicSelectKey,
      name: titleCaseWords(tab?.label || tab?.id || `Topic ${index + 1}`),
      perf: 0,
      importance: 0,
      negativity: 0,
      scopeReviewCount: reviewCounts.scopeReviewCount,
      citedReviewCount: reviewCounts.citedReviewCount,
      showCitedAsPrimary: reviewCounts.showCitedAsPrimary,
      sampleSize: reviewCounts.scopeReviewCount,
      representativeReviewIds: [],
      finding: parsed.finding,
      meaning: parsed.meaning,
      action: parsed.action,
      pending: Boolean(tab?.payload_pending || tab?.content_pending),
      quadrant: "Strategic",
      status: "Orta",
    };
  });
}

export const PivonyInsightWidget = ({
  items = [],
  tabs = [],
  summaryText = "",
  onTopicSelect,
  activeTopicId,
  widget = null,
  widgetId = null,
}) => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState("");
  const [reviewsDrawerOpen, setReviewsDrawerOpen] = useState(false);
  const [reviewsDrawerMode, setReviewsDrawerMode] = useState("scope");
  const [topicReviews, setTopicReviews] = useState([]);
  const [topicReviewsLoading, setTopicReviewsLoading] = useState(false);
  const categories = useMemo(
    () => buildCategories(items, tabs, summaryText),
    [items, tabs, summaryText],
  );
  const [activeId, setActiveId] = useState(() => categories[0]?.id || null);

  const filteredCategories = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter((item) => item.name.toLowerCase().includes(term));
  }, [query, categories]);

  const activeCategory = useMemo(() => {
    const current = filteredCategories.find((item) => item.id === activeId);
    if (current) return current;
    return filteredCategories[0] || categories[0] || null;
  }, [activeId, filteredCategories, categories]);

  const activeQuadrant = activeCategory?.quadrant || "Strategic";
  const activePosition = getQuadrantPosition(activeQuadrant);
  const locale = getIntlLocale(i18n.language);

  const topicLibForReviews = useMemo(
    () => resolveTopicLibIdForReviews(widget, activeCategory?.topicSelectKey),
    [widget, activeCategory],
  );
  const representativeReviewIds = useMemo(() => {
    const raw = activeCategory?.representativeReviewIds;
    return Array.isArray(raw) ? raw.filter(Boolean) : [];
  }, [activeCategory]);
  const wid = widgetId != null ? Number(widgetId) : NaN;
  const scopeReviewCount = Number(activeCategory?.scopeReviewCount || 0);
  const citedReviewCount =
    activeCategory?.citedReviewCount != null
      ? Number(activeCategory.citedReviewCount)
      : null;
  const showCitedSubline =
    citedReviewCount != null &&
    citedReviewCount > 0 &&
    scopeReviewCount > 0 &&
    citedReviewCount !== scopeReviewCount &&
    !activeCategory?.showCitedAsPrimary;
  const canLinkScopeReviews =
    Number.isFinite(wid) && wid > 0 && scopeReviewCount > 0 && topicLibForReviews != null;
  const canLinkCitedReviews =
    Number.isFinite(wid) &&
    wid > 0 &&
    citedReviewCount != null &&
    citedReviewCount > 0 &&
    representativeReviewIds.length > 0;
  const canShowScopeAllInDrawer =
    reviewsDrawerMode === "cited" &&
    canLinkScopeReviews &&
    citedReviewCount != null &&
    scopeReviewCount > citedReviewCount;

  const fetchTopicReviews = useCallback(
    async (mode) => {
      if (!Number.isFinite(wid) || wid <= 0) return;
      const useCited =
        mode === "cited" &&
        representativeReviewIds.length > 0 &&
        citedReviewCount != null &&
        citedReviewCount > 0;
      if (!useCited && topicLibForReviews == null) {
        if (representativeReviewIds.length === 0) return;
      }
      setReviewsDrawerMode(useCited ? "cited" : "scope");
      setReviewsDrawerOpen(true);
      setTopicReviewsLoading(true);
      try {
        const body = { widget_id: wid };
        if (useCited) {
          body.review_ids = representativeReviewIds;
        } else if (topicLibForReviews != null) {
          body.topic_lib_id = topicLibForReviews;
        } else if (representativeReviewIds.length > 0) {
          body.review_ids = representativeReviewIds;
        }
        const { reviews } = await fetchWelcomeWidgetMetricReviews(23, body);
        setTopicReviews(reviews);
      } catch {
        setTopicReviews([]);
      } finally {
        setTopicReviewsLoading(false);
      }
    },
    [wid, topicLibForReviews, representativeReviewIds, citedReviewCount],
  );

  const handleReviewsDrawerClose = useCallback(() => {
    setReviewsDrawerOpen(false);
    setTopicReviews([]);
    setTopicReviewsLoading(false);
    setReviewsDrawerMode("scope");
  }, []);

  useWelcomeReviewsDrawerCloseEffect(reviewsDrawerOpen, handleReviewsDrawerClose);

  const getQuadrantLabel = (quadrant) => {
    if (quadrant === "Urgent") return t("welcome.pivonyInsightQuadrantUrgent");
    if (quadrant === "Keep") return t("welcome.pivonyInsightQuadrantKeep");
    if (quadrant === "Improve") return t("welcome.pivonyInsightQuadrantImprove");
    return t("welcome.pivonyInsightQuadrantStrategic");
  };

  useEffect(() => {
    if (activeTopicId == null) return;
    const key = String(activeTopicId);
    const match = categories.find(
      (c) => c.topicSelectKey === key || String(c.id) === key,
    );
    if (match) setActiveId(match.id);
    else setActiveId(key);
  }, [activeTopicId, categories]);

  useEffect(() => {
    if (!categories.length) return;
    const hasActive = categories.some((item) => String(item.id) === String(activeId));
    if (!hasActive) setActiveId(categories[0].id);
  }, [categories, activeId]);

  const handleSelectCategory = (categoryId) => {
    setActiveId(categoryId);
    if (typeof onTopicSelect !== "function") return;
    const row = categories.find((c) => String(c.id) === String(categoryId));
    onTopicSelect(row?.topicSelectKey ?? categoryId);
  };

  if (!activeCategory) {
    return <div className={styles.emptyContent}>{t("welcome.pivonyInsightNoData")}</div>;
  }

  return (
    <section className={styles.widget}>
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h3 className={styles.sidebarTitle}>{t("welcome.pivonyInsightSidebarTitle")}</h3>
            <label className={styles.searchWrap}>
              <Search
                size={16}
                className={styles.searchIcon}
              />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className={styles.searchInput}
                placeholder={t("welcome.pivonyInsightSearchPlaceholder")}
              />
            </label>
          </div>

          <div className={styles.categoryList}>
            {filteredCategories.length === 0 ? (
              <div className={styles.emptyState}>
                {t("welcome.pivonyInsightNoResults")}
              </div>
            ) : null}

            {filteredCategories.map((item) => {
              const isActive = String(item.id) === String(activeCategory?.id);
              const statusMeta = getStatusMeta(item.status);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSelectCategory(item.id)}
                  className={`${styles.categoryButton} ${isActive ? styles.categoryButtonActive : ""}`}
                  aria-pressed={isActive}
                >
                  <div className={styles.categoryInner}>
                    <span className={styles.categoryName}>{item.name}</span>
                    <span
                      className={`${styles.badge} ${styles[statusMeta.classKey] || ""}`}
                    >
                      {t(statusMeta.labelKey)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <main className={styles.content}>
          <div className={styles.topArea}>
            <div className={styles.headline}>
              <h2 className={styles.categoryTitle}>{activeCategory.name}</h2>
              <p className={styles.categorySubtitle}>
                {t("welcome.pivonyInsightSubtitle")}
              </p>

              <div className={styles.metricGrid}>
                <div className={`${styles.metricPill} ${styles.metricBlue}`}>
                  <MetricLabelWithInfo
                    label={t("welcome.pivonyInsightMetricPerformance")}
                    metricKey="performance"
                    t={t}
                  />
                  <div className={styles.metricValueBlue}>
                    {formatPercent(activeCategory.perf, locale)}
                  </div>
                </div>
                <div className={`${styles.metricPill} ${styles.metricAmber}`}>
                  <MetricLabelWithInfo
                    label={t("welcome.pivonyInsightMetricImportance")}
                    metricKey="importance"
                    t={t}
                  />
                  <div className={styles.metricValueAmber}>
                    {formatImportanceScore(activeCategory.importance, locale)}
                  </div>
                </div>
                <div className={`${styles.metricPill} ${styles.metricRed}`}>
                  <MetricLabelWithInfo
                    label={t("welcome.pivonyInsightMetricNegativity")}
                    metricKey="negativity"
                    t={t}
                  />
                  <div className={styles.metricValueRed}>
                    {formatPercent(activeCategory.negativity, locale)}
                  </div>
                </div>
              </div>
              {scopeReviewCount > 0 || (citedReviewCount != null && citedReviewCount > 0) ? (
                <div className={styles.sampleMeta}>
                  {scopeReviewCount > 0 && !activeCategory?.showCitedAsPrimary ? (
                    canLinkScopeReviews ? (
                      <button
                        type="button"
                        className={styles.sampleReviewsLink}
                        onClick={() => fetchTopicReviews("scope")}
                        disabled={topicReviewsLoading}
                      >
                        {t("welcome.brandKpiReviewCount", {
                          count: scopeReviewCount,
                        })}
                      </button>
                    ) : (
                      t("welcome.brandKpiReviewCount", {
                        count: scopeReviewCount,
                      })
                    )
                  ) : null}
                  {activeCategory?.showCitedAsPrimary && citedReviewCount != null ? (
                    canLinkCitedReviews ? (
                      <button
                        type="button"
                        className={styles.sampleReviewsLink}
                        onClick={() => fetchTopicReviews("cited")}
                        disabled={topicReviewsLoading}
                      >
                        {t("welcome.pivonyInsightCitedReviewCount", {
                          count: citedReviewCount,
                        })}
                      </button>
                    ) : (
                      t("welcome.pivonyInsightCitedReviewCount", {
                        count: citedReviewCount,
                      })
                    )
                  ) : null}
                  {showCitedSubline ? (
                    <div className={styles.sampleCitedMeta}>
                      {canLinkCitedReviews ? (
                        <button
                          type="button"
                          className={styles.sampleReviewsLink}
                          onClick={() => fetchTopicReviews("cited")}
                          disabled={topicReviewsLoading}
                        >
                          {t("welcome.pivonyInsightCitedReviewCount", {
                            count: citedReviewCount,
                          })}
                        </button>
                      ) : (
                        t("welcome.pivonyInsightCitedReviewCount", {
                          count: citedReviewCount,
                        })
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className={styles.matrix}>
              <div className={styles.matrixHeader}>
                {t("welcome.pivonyInsightMatrixTitle")}
              </div>
              <div className={styles.matrixGrid}>
                {QUADRANT_LABELS.map((row, rowIndex) =>
                  row.map((label, colIndex) => {
                    const isActive = activePosition.row === rowIndex && activePosition.col === colIndex;
                    return (
                      <div
                        key={`${label}-${rowIndex}-${colIndex}`}
                        className={styles.matrixCell}
                      >
                        <div className={styles.matrixLabel}>{getQuadrantLabel(label)}</div>
                        <div className={styles.matrixDotWrap}>
                          {isActive ? (
                            <span
                              className={`${styles.matrixDot} ${
                                styles[quadrantDotClasses[activeQuadrant] || quadrantDotClasses.Strategic]
                              }`}
                            />
                          ) : (
                            <span className={`${styles.matrixDot} ${styles.dotInactive}`} />
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <section className={styles.insightBox}>
            <h3 className={styles.insightTitle}>{t("welcome.pivonyInsightBoxTitle")}</h3>

            <div className={styles.insightSections}>
              <div>
                <div className={styles.sectionTitle}>{t("welcome.pivonyInsightFindingTitle")}</div>
                <p className={styles.sectionText}>
                  {activeCategory.pending ? t("welcome.pivonyInsightLoading") : activeCategory.finding || "-"}
                </p>
              </div>
              <div>
                <div className={styles.sectionTitle}>{t("welcome.pivonyInsightMeaningTitle")}</div>
                <p className={styles.sectionText}>
                  {activeCategory.pending ? t("welcome.pivonyInsightLoading") : activeCategory.meaning || "-"}
                </p>
              </div>
              <div>
                <div className={styles.sectionTitle}>{t("welcome.pivonyInsightActionTitle")}</div>
                <p className={styles.sectionText}>
                  {activeCategory.pending ? t("welcome.pivonyInsightLoading") : activeCategory.action || "-"}
                </p>
              </div>
            </div>

            <button
              type="button"
              className={styles.actionButton}
            >
              {t("welcome.pivonyInsightActionButton")}
            </button>
          </section>
        </main>
      </div>

      <Drawer
        {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
        title={activeCategory?.name || t("welcome.highlightsDashReviewsTitle")}
        extra={
          <button
            type="button"
            className="welcome-reviews-drawer-header-close"
            onClick={handleReviewsDrawerClose}
          >
            {t("common.close", "Close")}
          </button>
        }
        onClose={handleReviewsDrawerClose}
        open={reviewsDrawerOpen}
        width={welcomeReviewsDrawerWidth()}
      >
        <Spin spinning={topicReviewsLoading}>
          {canShowScopeAllInDrawer ? (
            <button
              type="button"
              className={styles.viewAllScopeReviewsButton}
              onClick={() => fetchTopicReviews("scope")}
              disabled={topicReviewsLoading}
            >
              {t("welcome.pivonyInsightViewAllScopeReviews", {
                count: scopeReviewCount,
              })}
            </button>
          ) : null}
          <WelcomeDrawerReviewsList
            allReviews={topicReviews}
            reviewsLoading={topicReviewsLoading}
            showTotalCount
            exportFileLabel={activeCategory?.name}
          />
        </Spin>
      </Drawer>
    </section>
  );
};

export default PivonyInsightWidget;
